import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { jsPDF } from "jspdf";
import { FileOutput, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { loadSignatureFont } from "./signatureFont";

const today = () => new Date().toISOString().slice(0, 10);
const userName = (u) => (u?.first_name && u?.last_name ? `${u.first_name} ${u.last_name}` : u?.display_name || "");

const LETTERHEAD_URL = "/media/letterhead.png";
const LETTERHEAD_ASPECT = 148 / 651;

const makeDefaults = (user) => ({
  date: today(),
  reference: "",
  partDescription: "",
  partNumber: "",
  serialNumber: "",
  aircraftType: "",
  aircraftTail: "",
  signedName: userName(user),
  signedTitle: "Stores",
});

const BULLETS = [
  "Has not been involved in any known accident or incident;",
  "Has not been subjected to severe stress or heat (such as in a major engine failure, accident or fire);",
  "Has not been immersed in salt water;",
  "Has not been obtained from any military or government source.",
];

const F = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function NonIncidentForm() {
  const { user } = useAuth();
  const [m, setM] = useState(() => makeDefaults(user));
  const set = (k) => (e) => setM((s) => ({ ...s, [k]: e.target?.value ?? e }));
  const reset = () => setM(makeDefaults(user));

  const exportPdf = async () => {
    try {
      let lhData = null;
      try {
        const blob = await (await fetch(LETTERHEAD_URL)).blob();
        lhData = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsDataURL(blob);
        });
      } catch {}

      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const sigFont = await loadSignatureFont(doc);
      const ML = 22, MRX = 188, CW = MRX - ML;
      doc.setDrawColor(40);
      doc.setLineWidth(0.25);
      let y = 12;

      // Letterhead image
      if (lhData) {
        const lhH = CW * LETTERHEAD_ASPECT;
        doc.addImage(lhData, "PNG", ML, y, CW, lhH);
        y += lhH + 4;
      }
      y += 12;

      // Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("NON-INCIDENT STATEMENT", 105, y, { align: "center" });
      const tw = doc.getTextWidth("NON-INCIDENT STATEMENT");
      doc.line(105 - tw / 2, y + 1.5, 105 + tw / 2, y + 1.5);
      y += 14;

      // Date / reference
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      doc.text(`Date: ${m.date || ""}`, MRX, y, { align: "right" });
      if (m.reference) doc.text(`Reference: ${m.reference}`, ML, y);
      y += 12;

      doc.setFont("helvetica", "bold");
      doc.text("TO WHOM IT MAY CONCERN:", ML, y);
      y += 9;

      doc.setFont("helvetica", "normal");
      doc.text("This is to certify that the following part:", ML, y);
      y += 7;

      // Part details box
      const rows = [
        ["Part Description", m.partDescription],
        ["Part Number", m.partNumber],
        ["Serial Number", m.serialNumber],
        ["Aircraft Type (removed from)", m.aircraftType],
        ["Aircraft Registration / Tail #", m.aircraftTail],
      ];
      const rh = 9;
      doc.rect(ML, y, CW, rh * rows.length);
      doc.line(ML + 72, y, ML + 72, y + rh * rows.length);
      rows.forEach(([l, v], i) => {
        const ry = y + i * rh;
        if (i > 0) doc.line(ML, ry, MRX, ry);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(90);
        doc.text(l.toUpperCase(), ML + 2.5, ry + 5.6);
        doc.setTextColor(40);
        doc.setFontSize(10.5);
        doc.text(String(v || ""), ML + 76, ry + 5.9);
        doc.setTextColor(0);
      });
      y += rh * rows.length + 10;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      doc.text("to the best of our knowledge and belief:", ML, y);
      y += 8;
      BULLETS.forEach((b) => {
        const lines = doc.splitTextToSize(b, CW - 10);
        doc.text("\u2022", ML + 3, y);
        doc.text(lines, ML + 9, y);
        y += lines.length * 5 + 2.5;
      });
      y += 5;

      const closing = "This statement is issued by Summit Air in respect of the part detailed above.";
      const cl = doc.splitTextToSize(closing, CW);
      doc.text(cl, ML, y);
      y += cl.length * 5 + 22;

      // Signature block
      if (sigFont && m.signedName) {
        doc.setFont("signature", "normal");
        doc.setFontSize(24);
        doc.setTextColor(30);
        doc.text(m.signedName, ML + 3, y - 2);
        doc.setTextColor(0);
      }
      doc.line(ML, y, ML + 70, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(90);
      doc.text("SIGNATURE", ML, y + 4.5);
      doc.setTextColor(0);
      y += 12;
      doc.setFontSize(10.5);
      doc.setFont("helvetica", "bold");
      if (m.signedName) { doc.text(m.signedName, ML, y); y += 5.5; }
      doc.setFont("helvetica", "normal");
      if (m.signedTitle) { doc.text(m.signedTitle, ML, y); y += 5.5; }
      doc.text(`Date: ${m.date || ""}`, ML, y);

      doc.save(`Non-Incident Statement${m.partNumber ? ` ${m.partNumber}` : ""}${m.serialNumber ? ` SN ${m.serialNumber}` : ""} - ${m.date}.pdf`);
      toast.success("PDF exported");
    } catch {
      toast.error("PDF export failed");
    }
  };

  const inputCls = "h-9";
  return (
    <div data-testid="non-incident-form">
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={reset} data-testid="nif-reset-btn">
          <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
        </Button>
        <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={exportPdf} data-testid="nif-export-btn">
          <FileOutput className="w-4 h-4 mr-1.5" /> Export PDF
        </Button>
      </div>

      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        <div className="border border-summit-hairline rounded-lg bg-white p-4 space-y-3">
          <F label="Company letterhead (printed on the PDF)">
            <img src={LETTERHEAD_URL} alt="Summit Air letterhead" className="border border-summit-hairline rounded-md w-full max-w-md" data-testid="nif-letterhead-preview" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Date"><Input className={inputCls} type="date" value={m.date} onChange={set("date")} data-testid="nif-date" /></F>
            <F label="Reference (optional)"><Input className={inputCls} value={m.reference} onChange={set("reference")} data-testid="nif-reference" /></F>
            <F label="Signed by (print name)"><Input className={inputCls} value={m.signedName} onChange={set("signedName")} data-testid="nif-signed-name" /></F>
            <F label="Title"><Input className={inputCls} value={m.signedTitle} onChange={set("signedTitle")} data-testid="nif-signed-title" /></F>
          </div>
        </div>
        <div className="border border-summit-hairline rounded-lg bg-white p-4 grid grid-cols-2 gap-3 content-start">
          <F label="Part Description" className="col-span-2">
            <Input className={inputCls} value={m.partDescription} onChange={set("partDescription")} placeholder="e.g. Controller, Altitude Preselect" data-testid="nif-part-description" />
          </F>
          <F label="Part Number"><Input className={inputCls} value={m.partNumber} onChange={set("partNumber")} placeholder="e.g. 7004577-903" data-testid="nif-part-number" /></F>
          <F label="Serial Number"><Input className={inputCls} value={m.serialNumber} onChange={set("serialNumber")} placeholder="e.g. 87040476" data-testid="nif-serial-number" /></F>
          <F label="Aircraft Type (removed from)"><Input className={inputCls} value={m.aircraftType} onChange={set("aircraftType")} placeholder="e.g. DHC-8" data-testid="nif-aircraft-type" /></F>
          <F label="Aircraft Tail #"><Input className={inputCls} value={m.aircraftTail} onChange={set("aircraftTail")} placeholder="e.g. C-FSOU" data-testid="nif-aircraft-tail" /></F>
        </div>
      </div>

      <div className="mt-4 border border-summit-hairline rounded-lg bg-white p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Statement printed on the PDF</div>
        <p className="text-sm text-gray-600 mt-2">
          This is to certify that the part above, to the best of our knowledge and belief:
        </p>
        <ul className="mt-1.5 space-y-1 text-sm text-gray-600 list-disc list-inside">
          {BULLETS.map((b) => <li key={b}>{b}</li>)}
        </ul>
      </div>
    </div>
  );
}
